import { DsmPageContainer } from '@/components';
import PageHeader from '@/components/PageHeader';
import { getUserFacingErrorMessage } from '@/services/helpers';
import { useCreateConstructionAdderMutation } from '@/services/pricing';
import { ProductLine } from '@/services/productLines';
import { PalmettoButton, PalmettoIcon, PalmettoSelect, PalmettoTextInput } from '@palmetto/dsm-components';
import { toast } from '@palmetto/palmetto-components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Control, Controller, useFieldArray, useForm, UseFieldArrayRemove } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { ProgramType } from 'types';

import {
  batteryRowLabel,
  capSchedulePageTitle,
  electricalAdderPayload,
  MAX_CAP_ROWS,
  priceCapsListPath,
  rejectedProductLineIds,
  supportsCapSchedules,
  validateCapAmount,
  validateElectricalAdder,
} from './capSchedules';
import { useProductLineNames } from './CapSchedulesSummary';

// Field arrays need objects, so each cap row wraps its amount. Inputs hold strings until submit.
interface CapRowValue {
  amount: string;
}

interface ScheduleFormValue {
  productLineId: string;
  electricalAdder: string;
  caps: CapRowValue[];
}

export interface CapScheduleFormValues {
  name: string;
  schedules: ScheduleFormValue[];
}

const defaultValues: CapScheduleFormValues = { name: '', schedules: [] };

const nameRules = { validate: (value: string) => value.trim() !== '' || 'Name is required' };
const electricalAdderRules = { validate: validateElectricalAdder };
const capRules = { validate: validateCapAmount };
const schedulesRules = {
  validate: (schedules: ScheduleFormValue[]) => schedules.length > 0 || 'Add at least one product line',
};
const capsRules = { minLength: 1, maxLength: MAX_CAP_ROWS };

const addIcon = <PalmettoIcon name="add" size="sm" />;

const errorMessage = (message?: string) => (message ? { type: 'error' as const, text: message } : undefined);

interface CapRowProps {
  control: Control<CapScheduleFormValues>;
  scheduleIndex: number;
  rowIndex: number;
  rowCount: number;
  remove: UseFieldArrayRemove;
  disabled: boolean;
}

const CapRow = ({ control, scheduleIndex, rowIndex, rowCount, remove, disabled }: CapRowProps) => {
  const label = batteryRowLabel(rowIndex, rowCount);
  const handleRemove = useCallback(() => remove(rowIndex), [remove, rowIndex]);

  return (
    <div className="flex items-start gap-8">
      <div className="min-w-0 flex-1">
        <Controller
          name={`schedules.${scheduleIndex}.caps.${rowIndex}.amount`}
          control={control}
          rules={capRules}
          render={({ field, fieldState }) => (
            <PalmettoTextInput
              id={`cap-${scheduleIndex}-${rowIndex}`}
              label={label}
              type="number"
              inputMode="numeric"
              min={0}
              step={1}
              prefix="$"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              validationMessage={errorMessage(fieldState.error?.message)}
              disabled={disabled}
            />
          )}
        />
      </div>
      {rowCount > 1 && (
        <PalmettoButton
          type="button"
          emphasis="tertiary"
          layout="hug"
          aria-label={`Remove ${label}`}
          onClick={handleRemove}
          disabled={disabled}
        >
          <PalmettoIcon name="trash" size="sm" />
        </PalmettoButton>
      )}
    </div>
  );
};

interface ScheduleSectionProps {
  control: Control<CapScheduleFormValues>;
  index: number;
  productLineId: string;
  productLineName: string;
  isRejected: boolean;
  remove: UseFieldArrayRemove;
  disabled: boolean;
}

const ScheduleSection = ({
  control,
  index,
  productLineId,
  productLineName,
  isRejected,
  remove,
  disabled,
}: ScheduleSectionProps) => {
  const {
    fields,
    append,
    remove: removeRow,
  } = useFieldArray({
    control,
    name: `schedules.${index}.caps`,
    rules: capsRules,
  });

  const handleRemoveSection = useCallback(() => remove(index), [remove, index]);
  const handleAddRow = useCallback(() => append({ amount: '' }), [append]);

  const sectionClassName = `flex flex-col gap-12 rounded-8 border p-16 ${
    isRejected ? 'border-border-error' : 'border-border-default'
  }`;

  return (
    <div className={sectionClassName} data-testid={`cap-schedule-${productLineId}`}>
      <div className="flex items-center justify-between gap-8">
        <h3 className="heading-200 text-text-primary">{productLineName}</h3>
        <PalmettoButton
          type="button"
          emphasis="tertiary"
          size="small"
          layout="hug"
          aria-label={`Remove ${productLineName}`}
          onClick={handleRemoveSection}
          disabled={disabled}
        >
          Remove
        </PalmettoButton>
      </div>
      {isRejected && (
        <p role="alert" className="body-200 text-text-error">
          This product line is inactive or no longer exists.
        </p>
      )}
      <Controller
        name={`schedules.${index}.electricalAdder`}
        control={control}
        rules={electricalAdderRules}
        render={({ field, fieldState }) => (
          <PalmettoTextInput
            id={`electrical-adder-${index}`}
            label="Electrical adder"
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            prefix="$"
            helperText="Added once per project, regardless of battery count. Leave blank for none."
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            validationMessage={errorMessage(fieldState.error?.message)}
            disabled={disabled}
          />
        )}
      />
      <div className="flex flex-col gap-8">
        {fields.map((field, rowIndex) => (
          <CapRow
            key={field.id}
            control={control}
            scheduleIndex={index}
            rowIndex={rowIndex}
            rowCount={fields.length}
            remove={removeRow}
            disabled={disabled}
          />
        ))}
      </div>
      {fields.length < MAX_CAP_ROWS && (
        <div>
          <PalmettoButton
            type="button"
            emphasis="secondary"
            size="small"
            layout="hug"
            leadingIcon={addIcon}
            onClick={handleAddRow}
            disabled={disabled}
          >
            Battery
          </PalmettoButton>
        </div>
      )}
    </div>
  );
};

interface ProductLinePickerProps {
  productLines: ProductLine[];
  addedIds: string[];
  isLoading: boolean;
  disabled: boolean;
  onAdd: (productLineId: string) => void;
}

const ProductLinePicker = ({ productLines, addedIds, isLoading, disabled, onAdd }: ProductLinePickerProps) => {
  // A fresh items array after each add also resets the select back to its placeholder.
  const items = useMemo(() => {
    const added = new Set(addedIds);
    return productLines
      .filter((line) => !added.has(line.id))
      .map((line) => ({ label: line.name, value: line.id, selected: false }))
      .toSorted((a, b) => a.label.localeCompare(b.label));
  }, [productLines, addedIds]);

  const handleValueChange = useCallback(
    (details: { value: string[] }) => {
      const [productLineId] = details.value;
      if (productLineId) onAdd(productLineId);
    },
    [onAdd],
  );

  const uncoveredCount = items.length;

  return (
    <div className="flex flex-col gap-4">
      <PalmettoSelect
        label="Add product line"
        placeholder={isLoading ? 'Loading product lines...' : 'Select a product line'}
        items={items}
        onValueChange={handleValueChange}
        disabled={disabled || isLoading || items.length === 0}
      />
      {!isLoading && uncoveredCount > 0 && addedIds.length > 0 && (
        <p className="body-200 text-text-tertiary">
          {uncoveredCount} product {uncoveredCount === 1 ? 'line' : 'lines'} without caps
        </p>
      )}
    </div>
  );
};

interface CapScheduleFormProps {
  programType: ProgramType;
  priceCapType: string;
}

const CapScheduleForm = ({ programType, priceCapType }: CapScheduleFormProps) => {
  const navigate = useNavigate();
  const [createConstructionAdder] = useCreateConstructionAdderMutation();
  const { productLines, namesById, isLoading } = useProductLineNames();
  const [rejectedIds, setRejectedIds] = useState<string[]>([]);
  const listPath = priceCapsListPath(programType, priceCapType);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CapScheduleFormValues>({ defaultValues });
  const { fields, append, remove } = useFieldArray({ control, name: 'schedules', rules: schedulesRules });

  const addedIds = useMemo(() => fields.map((field) => field.productLineId), [fields]);

  const handleAddProductLine = useCallback(
    (productLineId: string) => append({ productLineId, electricalAdder: '', caps: [{ amount: '' }] }),
    [append],
  );
  const handleCancel = useCallback(() => navigate(listPath), [navigate, listPath]);

  const onSubmit = useCallback(
    async (values: CapScheduleFormValues) => {
      setRejectedIds([]);
      try {
        await createConstructionAdder({
          data: {
            adderType: priceCapType,
            name: values.name.trim(),
            energyBackupCapSchedules: values.schedules.map((schedule) => ({
              productLineId: schedule.productLineId,
              caps: schedule.caps.map((cap) => Number(cap.amount)),
              ...electricalAdderPayload(schedule.electricalAdder),
            })),
          },
          programType,
        }).unwrap();
        toast.success('Cap schedule created');
        navigate(listPath);
      } catch (error) {
        setRejectedIds(rejectedProductLineIds(error));
        toast.error(getUserFacingErrorMessage(error, 'Error creating cap schedule'));
      }
    },
    [createConstructionAdder, priceCapType, programType, navigate, listPath],
  );

  const pageTitle = capSchedulePageTitle(priceCapType);
  const schedulesError = errors.schedules?.root?.message;

  return (
    <DsmPageContainer title={pageTitle}>
      <PageHeader title={pageTitle} />

      <form onSubmit={handleSubmit(onSubmit)} className="mx-6 flex flex-col gap-24">
        <div className="max-w-[480px]">
          <Controller
            name="name"
            control={control}
            rules={nameRules}
            render={({ field, fieldState }) => (
              <PalmettoTextInput
                id="cap-schedule-name"
                label="Name"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                validationMessage={errorMessage(fieldState.error?.message)}
                disabled={isSubmitting}
              />
            )}
          />
        </div>

        <div className="flex flex-col gap-16">
          <div className="flex flex-col gap-4">
            <h2 className="heading-200 text-text-primary">Product line caps</h2>
            <p className="body-200 text-text-secondary">
              Each cap applies to one battery, not a running total. The last cap applies to every additional battery.
            </p>
          </div>
          {fields.map((field, index) => (
            <ScheduleSection
              key={field.id}
              control={control}
              index={index}
              productLineId={field.productLineId}
              productLineName={namesById.get(field.productLineId) ?? field.productLineId}
              isRejected={rejectedIds.includes(field.productLineId)}
              remove={remove}
              disabled={isSubmitting}
            />
          ))}
          {schedulesError && (
            <p role="alert" className="body-200 text-text-error">
              {schedulesError}
            </p>
          )}
          <div className="max-w-[480px]">
            <ProductLinePicker
              productLines={productLines}
              addedIds={addedIds}
              isLoading={isLoading}
              disabled={isSubmitting}
              onAdd={handleAddProductLine}
            />
          </div>
        </div>

        <div className="flex justify-end gap-8">
          <PalmettoButton
            type="button"
            emphasis="secondary"
            layout="hug"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </PalmettoButton>
          <PalmettoButton type="submit" emphasis="primary" layout="hug" loading={isSubmitting}>
            Save
          </PalmettoButton>
        </div>
      </form>
    </DsmPageContainer>
  );
};

export const CreateCapSchedule = () => {
  const navigate = useNavigate();
  const { programType = '', priceCapType = '' } = useParams<{ programType: string; priceCapType: string }>();
  const isSupported = supportsCapSchedules(programType, priceCapType);

  useEffect(() => {
    if (!isSupported) {
      navigate(`/admin/pricing/${programType}/price-caps`, { replace: true });
    }
  }, [isSupported, navigate, programType]);

  if (!isSupported) return null;

  return <CapScheduleForm programType={programType as ProgramType} priceCapType={priceCapType} />;
};
